import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useDebounce } from '@uidotdev/usehooks'
import { Drawer, Tooltip } from 'antd'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { DELAY } from '@/constants'
import useNomenclatureEditorSearch from '@/hooks/search/useNomenclatureEditorSearch'
import useSearch from '@/hooks/search/useSearch'
import useUpdateFilters from '@/hooks/search/useUpdateFilters'
import { filtersService } from '@/services/filters/FiltersService'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useNomenclatureEditorFeedStore } from '@/stores/feed/nomenclatureEditorFeedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  AvailableFiltersType,
  CharacteristicFilterType,
  EntityFilterType,
  StoreShortWithSupplierType,
} from '@/types'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { cleanTechnicalSuffix } from '@/utils/catalog/cleanDescription'
import {
  getSlotOptionSearchLabel,
  hasUniformEligibleSlottedFilter,
  isSlottedCharacteristicType,
} from '@/utils/search/characteristics/slottedCharacteristics'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import FilterByCodeWithList from './FilterByCodeWithList/FilterByCodeWithList'
import FilterRangeSlider from './FilterRangeSlider/FilterRangeSlider'
import cn from './Filters.module.scss'
import type { FilterChip } from './Filters.utils'
import {
  buildActiveFilterChips,
  formatCharacteristicValue,
  getIrrelevanceTooltip,
  getSelectedCharacteristicValues,
  removeCharacteristicByCode,
  sectionMatchesSearch,
  toggleCharacteristicOptionValue,
} from './Filters.utils'
import FilterSlotted from './FilterSlotted/FilterSlotted'
import FilterWithList from './FilterWithList/FilterWithList'
import FilterWithToggle from './FilterWithToggle/FilterWithToggle'

type FilterSection = {
  isApplied: boolean
  node: ReactNode
}

type FiltersPropsType = {
  open: boolean
  setOpen: (open: boolean) => void
}

function Filters({ open, setOpen }: FiltersPropsType) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const lastProcessedSearch = useRef<string>(searchParams.toString())

  const { selectedOrganisation, organisation, userOrganisation } =
    useUserInfoStore((state) => state)
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )

  const isFirstRun = useRef(true)

  const { setProductsLoading, setExpandedRows, setPaginatedTableLoading } =
    useFeedStore((state) => state)
  const feedPagination = useFeedStore((state) => state.pagination)
  const setNomenclatureEditorProductsLoading = useNomenclatureEditorFeedStore(
    (state) => state.setProductsLoading
  )

  const editorPagination = useNomenclatureEditorFeedStore(
    (state) => state.pagination
  )

  const search = useSearch()
  const nomenclatureEditorSearch = useNomenclatureEditorSearch()
  const updateFilters = useUpdateFilters()

  const {
    withDiscontinued,
    setWithDiscontinued,
    matchUniformSlottedCharacteristics,
    setMatchUniformSlottedCharacteristics,
    searchTerm,
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
    currentlySearchableNomenclatureGroupId,
    currentlySearchableParents,
    setCurrentlySearchableParents,
    availableFilters,
    withLeftovers,
    setCurrentlySearchableCategories,
    setCurrentlySearchableBrands,
    setCurrentlySearchableStores,
    setCurrentlySearchableCharacteristics,
    setSearchTerm,
    setWithLeftovers,
    setServerChosenFilters,
    serverChosenFilters,
  } = useSearchStore((state) => state)

  const [searchWithLeftovers, setSearchWithLeftovers] = useState(false)
  const [selectedStores, setSelectedStores] = useState<
    StoreShortWithSupplierType[]
  >([])

  const [searchWithDiscontinued, setSearchWithDiscontinued] = useState(true)
  const [searchMatchUniformSlotted, setSearchMatchUniformSlotted] =
    useState(false)
  const [selectedParents, setSelectedParents] = useState<EntityFilterType[]>([])

  const [selectedBrands, setSelectedBrands] = useState<EntityFilterType[]>([])
  const [selectedCategories, setSelectedCategories] = useState<
    EntityFilterType[]
  >([])
  const [selectedCharacteristics, setSelectedCharacteristics] = useState<
    CharacteristicFilterType[]
  >([])

  const [draftAvailableFilters, setDraftAvailableFilters] =
    useState<AvailableFiltersType>({} as AvailableFiltersType)
  const [draftTotal, setDraftTotal] = useState(0)
  const [isFiltersLoading, setIsFiltersLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // Ответы фасетного пересчёта могут вернуться не в том порядке, в каком ушли
  // запросы: без счётчика более старый ответ затирает более свежий, и в шторке
  // остаются значения от предыдущего выбора.
  const facetRequestIdRef = useRef(0)

  const activePagination = isEditor ? editorPagination : feedPagination

  const isDraftMatchUniformApplicable = useMemo(
    () => hasUniformEligibleSlottedFilter(selectedCharacteristics),
    [selectedCharacteristics]
  )

  useEffect(() => {
    if (searchMatchUniformSlotted && !isDraftMatchUniformApplicable) {
      setSearchMatchUniformSlotted(false)
    }
  }, [searchMatchUniformSlotted, isDraftMatchUniformApplicable])

  const hasChanges = useMemo(() => {
    if (!isEditor && searchWithLeftovers !== withLeftovers) return true
    if (isEditor && searchWithDiscontinued !== withDiscontinued) return true
    const effectiveDraftMatchUniform =
      searchMatchUniformSlotted && isDraftMatchUniformApplicable
    if (effectiveDraftMatchUniform !== matchUniformSlottedCharacteristics)
      return true

    const areEntitiesDifferent = (
      arr1: { id: string }[],
      arr2: { id: string }[]
    ) => {
      if (arr1.length !== arr2.length) return true
      const set2 = new Set(arr2.map((item) => item.id))
      return arr1.some((item) => !set2.has(item.id))
    }

    if (areEntitiesDifferent(selectedBrands, currentlySearchableBrands))
      return true
    if (areEntitiesDifferent(selectedCategories, currentlySearchableCategories))
      return true
    if (areEntitiesDifferent(selectedStores, currentlySearchableStores))
      return true
    if (
      isEditor &&
      areEntitiesDifferent(selectedParents, currentlySearchableParents)
    )
      return true

    const simplifyChars = (chars: CharacteristicFilterType[]) =>
      chars
        .map((c) => ({
          code: c.code,
          options: (c.options || []).map((o) => o.value).sort(),
          range: c.chosenRange
            ? { min: c.chosenRange.min, max: c.chosenRange.max }
            : null,
        }))
        .sort((a, b) => a.code - b.code)

    const draftChars = JSON.stringify(simplifyChars(selectedCharacteristics))
    const appliedChars = JSON.stringify(
      simplifyChars(currentlySearchableCharacteristics)
    )

    return draftChars !== appliedChars
  }, [
    searchWithLeftovers,
    withLeftovers,
    searchWithDiscontinued,
    withDiscontinued,
    searchMatchUniformSlotted,
    isDraftMatchUniformApplicable,
    matchUniformSlottedCharacteristics,
    selectedBrands,
    currentlySearchableBrands,
    selectedCategories,
    currentlySearchableCategories,
    selectedStores,
    currentlySearchableStores,
    selectedParents,
    currentlySearchableParents,
    selectedCharacteristics,
    currentlySearchableCharacteristics,
    isEditor,
  ])

  useEffect(() => {
    if (open) {
      const syncedState = filtersService.state.syncWithGlobalState(
        currentlySearchableBrands,
        currentlySearchableCategories,
        currentlySearchableStores,
        currentlySearchableCharacteristics,
        currentlySearchableParents,
        withLeftovers,
        withDiscontinued
      )

      setSearchWithDiscontinued(withDiscontinued)
      setSearchMatchUniformSlotted(matchUniformSlottedCharacteristics)
      setSelectedParents(currentlySearchableParents)
      setSelectedBrands(syncedState.selectedBrands)
      setSelectedCategories(syncedState.selectedCategories)
      setSearchWithLeftovers(syncedState.searchWithLeftovers)
      setSelectedCharacteristics(syncedState.selectedCharacteristics)
      setSelectedStores(syncedState.selectedStores)

      setDraftAvailableFilters(availableFilters)
      setDraftTotal(activePagination.total ?? 0)
      setSearchQuery('')
    }
  }, [open])

  const debouncedFiltersString = useDebounce(
    JSON.stringify({
      searchWithLeftovers,
      searchWithDiscontinued,
      selectedStores,
      selectedBrands,
      selectedCategories,
      selectedCharacteristics,
      selectedParents,
      searchMatchUniformSlotted,
    }),
    DELAY
  )

  useEffect(() => {
    if (!open) return

    const updateDraftState = async () => {
      const requestId = facetRequestIdRef.current + 1
      facetRequestIdRef.current = requestId

      setIsFiltersLoading(true)

      const draftState = {
        searchWithLeftovers: isEditor ? false : searchWithLeftovers,
        searchWithDiscontinued,
        selectedStores,
        selectedBrands,
        selectedCategories,
        selectedParents,
        selectedCharacteristics,
        searchMatchUniformSlotted,
      }

      const result = await filtersService.handleFacetedFilterUpdate(
        {
          searchTerm,
          draftState,
          appliedState: {
            withLeftovers: isEditor ? false : withLeftovers,
            withDiscontinued,
            stores: currentlySearchableStores,
            brands: currentlySearchableBrands,
            categories: currentlySearchableCategories,
            parents: currentlySearchableParents,
            characteristics: currentlySearchableCharacteristics,
            matchUniformSlottedCharacteristics,
          },
          availableFilters,
          totalElements: activePagination.total ?? 0,
        },
        updateFilters
      )

      // Пока ждали ответ, пользователь успел кликнуть ещё раз — этот ответ устарел
      if (facetRequestIdRef.current !== requestId) return

      if (result) {
        setDraftAvailableFilters(result.availableFilters)
        setDraftTotal(result.totalElements)
      }

      setIsFiltersLoading(false)
    }

    updateDraftState()
  }, [debouncedFiltersString, searchTerm, isEditor])

  useEffect(() => {
    if (!organisation || !Object.keys(organisation).length) return

    if (isEditor) return

    const isFeedPage =
      location.pathname.includes('/feed') || location.pathname === '/'
    if (!isFeedPage) return

    const currentSearchStr = searchParams.toString()

    if (
      isFirstRun.current ||
      currentSearchStr !== lastProcessedSearch.current
    ) {
      filtersService.handleInitialSearch({
        searchParams,
        organisation,
        location,
        setters: {
          setSearchTerm,
          setWithLeftovers,
          setCurrentlySearchableCategories,
          setCurrentlySearchableBrands,
          setCurrentlySearchableStores,
          setCurrentlySearchableCharacteristics,
          setServerChosenFilters,
          setProductsLoading,
          setPaginatedTableLoading,
          setExpandedRows,
          setCurrentlySearchableParents,
          setWithDiscontinued,
          setMatchUniformSlottedCharacteristics,
        },
        searchHook: search,
        navigate,
      })

      lastProcessedSearch.current = currentSearchStr
      isFirstRun.current = false
    }
  }, [userOrganisation, isEditor, searchParams])

  const draftStoresFromFilters = filtersService.transform.flattenStores(
    draftAvailableFilters
  )

  const chosenCharCodes = useMemo(
    () =>
      new Set((serverChosenFilters?.characteristics || []).map((c) => c.code)),
    [serverChosenFilters]
  )

  const mainFiltersCodes = useMemo(
    () =>
      new Set(
        (draftAvailableFilters.characteristics || [])
          .filter((c) => c.isMain === true)
          .map((c) => c.code)
      ),
    [draftAvailableFilters.characteristics]
  )

  const visibleCharacteristics = useMemo(
    () =>
      (draftAvailableFilters.characteristics || []).filter(
        (availableFilter) => !mainFiltersCodes.has(availableFilter.code)
      ),
    [draftAvailableFilters.characteristics, mainFiltersCodes]
  )

  const searchedVisibleCharacteristics = useMemo(() => {
    if (!searchQuery.trim()) return visibleCharacteristics
    return visibleCharacteristics.filter((c) =>
      sectionMatchesSearch(
        c.name,
        c.options.map((o) =>
          isSlottedCharacteristicType(c.type)
            ? getSlotOptionSearchLabel(String(o.value))
            : formatCharacteristicValue(o.value)
        ),
        searchQuery
      )
    )
  }, [visibleCharacteristics, searchQuery])

  const appliedCharCodes = new Set([
    ...chosenCharCodes,
    ...currentlySearchableCharacteristics.map((c) => c.code),
  ])

  const appliedValuesByCode = useMemo(
    () =>
      new Map(
        currentlySearchableCharacteristics.map((c) => [
          c.code,
          getSelectedCharacteristicValues(c),
        ])
      ),
    [currentlySearchableCharacteristics]
  )

  const characteristicTypeByCode = useMemo(
    () =>
      new Map(
        (draftAvailableFilters.characteristics || []).map((c) => [
          c.code,
          c.type,
        ])
      ),
    [draftAvailableFilters.characteristics]
  )

  const activeFilterChips = useMemo(
    () =>
      buildActiveFilterChips({
        selectedBrands,
        selectedStores,
        selectedParents: isEditor ? selectedParents : [],
        selectedCharacteristics,
        characteristicTypeByCode,
      }),
    [
      selectedBrands,
      selectedStores,
      selectedParents,
      selectedCharacteristics,
      isEditor,
      characteristicTypeByCode,
    ]
  )

  const handleRemoveChip = (chip: FilterChip) => {
    switch (chip.kind) {
      case 'brand':
        setSelectedBrands(selectedBrands.filter((b) => b.id !== chip.entityId))
        break
      case 'store':
        setSelectedStores(selectedStores.filter((s) => s.id !== chip.entityId))
        break
      case 'parent':
        setSelectedParents(
          selectedParents.filter((p) => p.id !== chip.entityId)
        )
        break
      case 'characteristic-option': {
        const characteristic = selectedCharacteristics.find(
          (c) => c.code === chip.characteristicCode
        )
        if (characteristic) {
          setSelectedCharacteristics(
            toggleCharacteristicOptionValue(
              selectedCharacteristics,
              characteristic,
              chip.optionValue
            )
          )
        }
        break
      }
      case 'characteristic-range':
      case 'characteristic-slotted':
        setSelectedCharacteristics(
          removeCharacteristicByCode(
            selectedCharacteristics,
            chip.characteristicCode
          )
        )
        break
    }
  }

  const closeFilters = () => {
    setOpen(false)
    focusProductSearchInput(350)
  }

  const handleApplyFilters = () => {
    filtersService.applyFilters({
      isNomenclatureEditor: isEditor,
      searchTerm,
      searchWithLeftovers,
      searchWithDiscontinued,
      searchMatchUniformSlottedCharacteristics: searchMatchUniformSlotted,
      selectedParents,
      selectedCategories,
      selectedBrands,
      selectedStores,
      selectedCharacteristics,
      currentlySearchableNomenclatureGroupId,
      setters: {
        setProductsLoading,
        setNomenclatureEditorProductsLoading,
        setCurrentlySearchableBrands,
        setCurrentlySearchableCategories,
        setCurrentlySearchableStores,
        setCurrentlySearchableCharacteristics,
        setWithLeftovers,
        setCurrentlySearchableParents,
        setWithDiscontinued,
        setMatchUniformSlottedCharacteristics,
      },
      searchHook: search,
      nomenclatureEditorSearchHook: nomenclatureEditorSearch,
    })
    closeFilters()
  }

  const handleResetFilters = () => {
    const resetState = filtersService.state.resetFilters()
    // setSelectedStores(resetState.selectedStores)
    // setSelectedBrands(resetState.selectedBrands)
    // setSelectedCategories(resetState.selectedCategories)
    setSelectedCharacteristics(resetState.selectedCharacteristics)
    // setSearchWithLeftovers(resetState.searchWithLeftovers)
    if (isEditor) {
      setSearchWithDiscontinued(resetState.searchWithDiscontinued)
      setSelectedParents(resetState.selectedParents)
    }
    setSearchMatchUniformSlotted(false)
  }

  const isApplyDisabled = !hasChanges

  const renderCharacteristicFilter = (
    availableFilter: CharacteristicFilterType,
    forceOpen: boolean
  ) => {
    const selectedChar = selectedCharacteristics.find(
      (sel) => sel.code === availableFilter.code
    )

    if (availableFilter.type === 'NUMERIC') {
      return (
        <FilterRangeSlider
          key={availableFilter.code}
          item={availableFilter}
          selectedItem={selectedChar}
          forceOpen={forceOpen}
          onChange={(range) => {
            setSelectedCharacteristics((prev) => {
              const filtered = removeCharacteristicByCode(
                prev,
                availableFilter.code
              )
              if (range === null) return filtered
              return [
                ...filtered,
                { ...availableFilter, chosenRange: range, options: [] },
              ]
            })
          }}
        />
      )
    }

    if (isSlottedCharacteristicType(availableFilter.type)) {
      return (
        <FilterSlotted
          key={availableFilter.code}
          item={availableFilter}
          selectedItems={selectedCharacteristics}
          title={cleanTechnicalSuffix(availableFilter.name)}
          collapseItemKey={availableFilter.name}
          setSelectedItems={setSelectedCharacteristics}
          forceOpen={forceOpen}
          searchQuery={searchQuery}
          matchUniform={searchMatchUniformSlotted}
          onMatchUniformChange={setSearchMatchUniformSlotted}
          pinnedValues={appliedValuesByCode.get(availableFilter.code)}
        />
      )
    }

    return (
      <FilterByCodeWithList
        key={availableFilter.code}
        item={availableFilter}
        selectedItems={selectedCharacteristics}
        title={cleanTechnicalSuffix(availableFilter.name)}
        collapseItemKey={availableFilter.name}
        setSelectedItems={setSelectedCharacteristics}
        forceOpen={forceOpen}
        searchQuery={searchQuery}
        pinnedValues={appliedValuesByCode.get(availableFilter.code)}
      />
    )
  }

  const isSearchingFilters = !!searchQuery.trim()

  const filterSections: FilterSection[] = []

  if (
    isEditor &&
    draftAvailableFilters.parents &&
    sectionMatchesSearch(
      'Номенклатурная группа',
      draftAvailableFilters.parents.map((p) => p.description),
      searchQuery
    )
  ) {
    const isApplied = currentlySearchableParents.length > 0
    filterSections.push({
      isApplied,
      node: (
        <FilterWithList<EntityFilterType>
          key="parents"
          items={draftAvailableFilters.parents}
          selectedItems={selectedParents}
          previouslySelectedItems={selectedParents}
          title="Номенклатурная группа"
          collapseItemKey="parents"
          searchInputPlaceholder="Название группы"
          setSelectedItems={setSelectedParents}
          forceOpen={isApplied || isSearchingFilters}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  if (
    draftAvailableFilters.brands &&
    sectionMatchesSearch(
      'Бренд',
      draftAvailableFilters.brands.map((b) => b.description),
      searchQuery
    )
  ) {
    const isApplied = currentlySearchableBrands.length > 0
    filterSections.push({
      isApplied,
      node: (
        <FilterWithList<EntityFilterType>
          key="brand"
          items={draftAvailableFilters.brands}
          selectedItems={selectedBrands}
          previouslySelectedItems={selectedBrands}
          title="Бренд"
          collapseItemKey="brand"
          withPills={false}
          searchInputPlaceholder="Название бренда"
          setSelectedItems={setSelectedBrands}
          forceOpen={isApplied || isSearchingFilters}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  if (
    !isEditor &&
    (draftStoresFromFilters.length > 0 || selectedStores.length > 0) &&
    sectionMatchesSearch(
      'Склад',
      [...draftStoresFromFilters, ...selectedStores].map((s) => s.description),
      searchQuery
    )
  ) {
    const isApplied = currentlySearchableStores.length > 0
    filterSections.push({
      isApplied,
      node: (
        <FilterWithList<StoreShortWithSupplierType>
          key="store"
          items={draftStoresFromFilters}
          selectedItems={selectedStores}
          previouslySelectedItems={selectedStores}
          title="Склад"
          collapseItemKey="store"
          withPills={false}
          searchInputPlaceholder="Название склада"
          setSelectedItems={setSelectedStores}
          forceOpen={isApplied || isSearchingFilters}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  searchedVisibleCharacteristics.forEach((availableFilter) => {
    const isApplied = appliedCharCodes.has(availableFilter.code)
    filterSections.push({
      isApplied,
      node: renderCharacteristicFilter(
        availableFilter,
        isApplied || isSearchingFilters
      ),
    })
  })

  const appliedSections = filterSections.filter((section) => section.isApplied)
  const otherSections = filterSections.filter((section) => !section.isApplied)

  return (
    <Drawer
      title="Фильтры"
      closable={{ 'aria-label': 'Close Button' }}
      open={open}
      onClose={closeFilters}
      rootClassName={
        Object.keys(selectedOrganisation).length
          ? [cn.drawer, cn.orgTheme].join(' ')
          : cn.drawer
      }
      width={376}
    >
      <div className={cn.filters}>
        <div className={cn.filterSearch}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Искать фильтр..."
          />
        </div>

        {activeFilterChips.length > 0 && (
          <div className={cn.chipStrip}>
            {activeFilterChips.map((chip) => {
              const isIrrelevant = chip.isRelevant === false
              return (
                <div
                  key={chip.id}
                  className={[cn.chip, isIrrelevant ? cn.chipIrrelevant : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span>{chip.label}</span>
                  {chip.irrelevance && (
                    <Tooltip title={getIrrelevanceTooltip(chip.irrelevance)}>
                      <QuestionCircleOutlined className={cn.chipInfoIcon} />
                    </Tooltip>
                  )}
                  <button
                    type="button"
                    className={cn.chipRemove}
                    onClick={() => handleRemoveChip(chip)}
                    aria-label={`Убрать фильтр: ${chip.label}`}
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {/* {role !== OWNER_ROLES.NOMENCLATURE_EDITOR && (
          <FilterWithToggle
            value={searchWithLeftovers}
            setValue={setSearchWithLeftovers}
            title="Только в наличии"
          />
        )} */}

        {isEditor && (
          <FilterWithToggle
            value={searchWithDiscontinued}
            setValue={setSearchWithDiscontinued}
            title="Включая товары, снятые с производства"
          />
        )}

        {/* {draftAvailableFilters.categories && (
          <FilterWithList<EntityFilterType>
            items={draftAvailableFilters.categories}
            selectedItems={selectedCategories}
            previouslySelectedItems={selectedCategories}
            title="Категория"
            collapseItemKey="category"
            searchInputPlaceholder="Название категории"
            setSelectedItems={setSelectedCategories}
          />
        )}  */}

        {[...appliedSections, ...otherSections].map((section) => section.node)}
      </div>
      <div
        className={
          Object.keys(selectedOrganisation).length
            ? [cn.filtersActionButtonsContainer, cn.organisationSelected].join(
                ' '
              )
            : cn.filtersActionButtonsContainer
        }
      >
        <button
          onClick={handleApplyFilters}
          disabled={isApplyDisabled}
          className={`${cn.applyButton} ${isApplyDisabled ? cn.applyButtonDisabled : ''}`}
        >
          {isFiltersLoading ? (
            <span className={cn.applyButtonLoading}>
              Обновление <LoadingOutlined />
            </span>
          ) : (
            `Показать (${draftTotal || 0})`
          )}
        </button>
        <button onClick={handleResetFilters}>Сбросить все</button>
      </div>
    </Drawer>
  )
}

export default Filters
